import { RUNTIME_OFFER_PLACEMENT_TIMING_LOOKUP_BELOW_RESULT } from "@/lib/offer-placement-runtime-keys.generated";
import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";
import DepthPaywall from "@/components/DepthPaywall";
import OfferImpression from "@/components/OfferImpression";
import ReportCollectInputsCTA from "@/components/ReportCollectInputsCTA";
import { buildReportInputHref, resolvePageReportOffer } from "@/lib/page-report-offer";
import { reportIntentOffer } from "@/lib/report-intent-offer";

/**
 * Intent-matched paid-report offer for TIMING lookup pages (panchang,
 * choghadiya, festival). These high-traffic SEO surfaces currently earn ₹0 —
 * this places the `muhurat_report` (₹149) offer BELOW the tool result so a
 * seeker who looked up a public timing window can get the real, chart-grounded
 * muhurat (rules 39/43/45/46: one offer, below the job, intent-matched).
 *
 * Acquisition mode: no birth inputs exist on a lookup page, so the CTA routes
 * to the birth-details form (`collectInputsHref`) instead of opening Razorpay
 * with incomplete report data. The plan + intent resolve through the same
 * source map that generates seo_page_plan_map.csv; plan display facts come from
 * the generated commerce snapshot, while `reportIntentOffer("muhurat-report")`
 * supplies the shared timing copy. Reach is metered by the render-once
 * `OfferImpression` island (rule 46/47): `PaidReportCTA`'s own mount effect is
 * short-circuited in acquisition mode (`collectInputsHref` set), so without the
 * island these timing pages fired ZERO `agent_revenue_offer_shown` and the
 * funnel read them as 0% reach + ₹0 despite the CTA being on screen. The island
 * keys on the stable plan/placement so it fires exactly once, matching clicks.
 *
 * Copy is pain-led per rule 22 ("why does this keep happening?") in rule-21
 * vocabulary (shubh muhurat, panchang, kundali).
 */
export default function TimingReportCTA({
  language = "en",
  sourcePath,
  source: _source,
  placement = RUNTIME_OFFER_PLACEMENT_TIMING_LOOKUP_BELOW_RESULT,
}: {
  language?: "en" | "hi";
  /** Override the page the offer is shown on (for accurate attribution). */
  sourcePath?: string;
  /** Legacy analytics source tag; collect-input clicks use analyticsContext. */
  source?: string;
  /** Stable surface-specific placement for independent reach/CTR measurement. */
  placement?: string;
}) {
  const offer = reportIntentOffer("muhurat-report");
  const resolvedSourcePath = sourcePath ?? offer.analyticsContext.sourcePath;
  const pageOffer = resolvePageReportOffer(resolvedSourcePath);
  const plan = reportPlanSummaryMap[pageOffer.planId];
  const routeRankReason = `timing_lookup_${pageOffer.planId}`;
  const collectInputsHref = buildReportInputHref({
    ...pageOffer,
    sourcePath: resolvedSourcePath,
    language,
  });

  const headline =
    language === "hi"
      ? "Aaj ka shubh time aapke liye sahi hai ya nahi?"
      : "Why does a public shubh time still feel wrong for you?";
  const subhead =
    language === "hi"
      ? "Public choghadiya sirf bahari layer hai. Apne purpose, city, Moon sign, dasha aur panchang ke saath personal Muhurat Report paayein."
      : "A public choghadiya is only the outer layer. Get a personal Muhurat Report that reads your purpose, city, Moon sign, dasha, and panchang together.";

  return (
    <div className="mx-auto my-8 max-w-md px-4 sm:px-6">
      <OfferImpression
        planId={pageOffer.planId}
        actionMode="input_collection"
        sourcePath={resolvedSourcePath}
        intent={pageOffer.intent}
        placement={placement}
        customerProblem={offer.analyticsContext.customerProblem}
        urgency={offer.analyticsContext.urgency}
        confidence={offer.analyticsContext.confidence}
        routeRankScore={offer.analyticsContext.routeRankScore}
        routeRankReason={routeRankReason}
        rankedOfferOrder={[pageOffer.planId]}
      />
      <DepthPaywall planId={pageOffer.planId} language={language}>
        <ReportCollectInputsCTA
          planId={pageOffer.planId}
          priceInr={plan.price_inr}
          language={language}
          headline={headline}
          headingLevel="h2"
          subhead={subhead}
          collectInputsHref={collectInputsHref}
          collectInputsLabel={offer.collectInputsLabel}
          collectInputsNote={offer.collectInputsNote}
          analyticsContext={{
            ...offer.analyticsContext,
            intent: pageOffer.intent,
            sourcePath: resolvedSourcePath,
            placement,
            routeRankReason,
            rankedOfferOrder: [pageOffer.planId],
          }}
        />
      </DepthPaywall>
    </div>
  );
}
